import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Plus, Calendar, DollarSign, Ticket, Edit, Trash2, QrCode, ScanLine, Mail, CheckCircle2, XCircle, Building2, Ban, AlertTriangle, Gift, Users, Search, Pencil, X as XIcon, Link2, Copy } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../lib/api';
import OnboardingChecklist from '../components/OnboardingChecklist';
import OrganiserProfileForm from '../components/OrganiserProfileForm';
import TeamSection from '../components/TeamSection';
import DemoModeBadge from '../components/DemoModeBadge';
import PayoutsSection from '../components/PayoutsSection';
import LocationPicker from '../components/LocationPicker';

const OrganiserDashboard = () => {
  const { user } = useAuth();
  // Persist the in-progress Create Event form to localStorage so a session
  // expiry, accidental tab close or refresh never wipes the organiser's work.
  const DRAFT_KEY = `vinepass:event-draft:${user?.user_id || 'anon'}`;
  const readDraft = () => {
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Ignore stale drafts older than 7 days.
      if (parsed?.savedAt && Date.now() - parsed.savedAt > 7 * 24 * 60 * 60 * 1000) {
        window.localStorage.removeItem(DRAFT_KEY);
        return null;
      }
      return parsed?.form || null;
    } catch {
      return null;
    }
  };
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [refundRequests, setRefundRequests] = useState([]);
  const [resendingVerify, setResendingVerify] = useState(false);
  const [cancelDialog, setCancelDialog] = useState({ open: false, event: null, reason: '', submitting: false });
  const [compDialog, setCompDialog] = useState({
    open: false,
    event: null,
    ticket_type_id: '',
    guests: [{ name: '', email: '' }],
    message: '',
    submitting: false,
  });
  const [guestListDialog, setGuestListDialog] = useState({
    open: false,
    event: null,
    loading: false,
    rows: [],
    total_checked_in: 0,
    search: '',
    editing: null, // { ticket_code, name, email }
    saving: false,
  });
  const [cancelTicketDialog, setCancelTicketDialog] = useState({
    open: false,
    row: null,
    refund: true,
    reason: '',
    submitting: false,
  });

  const [eventForm, setEventForm] = useState(() => {
    const draft = readDraft();
    return draft || {
      title: '',
      description: '',
      date: '',
      time: '',
      location: '',
      category: 'Wine Tastings',
      image_url: '',
      organiser_name: user?.name || '',
      latitude: null,
      longitude: null,
      booking_fee_strategy: 'customer_pays',
      visibility: 'public',
    };
  });
  const [hasDraft, setHasDraft] = useState(() => !!readDraft());

  // Auto-save the Create Event form whenever the user touches it. Cheap,
  // synchronous, debounced via a 400ms timer so we don't thrash storage on
  // every keystroke.
  useEffect(() => {
    const isEmpty = !eventForm.title && !eventForm.description && !eventForm.date
      && !eventForm.time && !eventForm.location && !eventForm.image_url;
    if (isEmpty) {
      try { window.localStorage.removeItem(DRAFT_KEY); } catch {}
      setHasDraft(false);
      return;
    }
    const t = setTimeout(() => {
      try {
        window.localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({ form: eventForm, savedAt: Date.now() }),
        );
        setHasDraft(true);
      } catch {
        // Quota errors etc. — silent.
      }
    }, 400);
    return () => clearTimeout(t);
  }, [eventForm, DRAFT_KEY]);

  const clearDraft = () => {
    try { window.localStorage.removeItem(DRAFT_KEY); } catch {}
    setHasDraft(false);
  };
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const [ticketForm, setTicketForm] = useState({
    type_name: '',
    price: '',
    quantity: '',
    description: '',
    age_restriction: '',
    is_free: false,
  });
  const [organiserProfile, setOrganiserProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileFormOpen, setProfileFormOpen] = useState(false);
  const [banking, setBanking] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchRefundRequests();
    fetchOrganiserProfile();
    fetchBanking();
  }, []);

  const fetchBanking = async () => {
    if (user?.role === 'associate') {
      setBanking(null);
      return;
    }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/organiser/banking`, { withCredentials: true });
      setBanking(res.data?.banking || null);
    } catch {
      setBanking(null);
    }
  };

  const fetchOrganiserProfile = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/organiser/profile`, { withCredentials: true });
      // Only overwrite state when the server actually returned a profile.
      // A transient 200-with-null (e.g. very-first login race) must not
      // wipe out a previously-loaded profile from the same session.
      if (res.data?.profile) {
        setOrganiserProfile(res.data.profile);
      }
    } catch {
      // Network / 401 blip — leave any previously-loaded profile intact.
      // The global axios interceptor already retries once after silent refresh.
    } finally {
      setProfileLoaded(true);
    }
  };

  // Whenever the organiser profile finishes loading (or is updated via the
  // Edit Profile modal), sync the persisted organisation_name into the
  // Create Event form so the organiser never has to re-type their brand
  // name for a new event.
  useEffect(() => {
    if (!organiserProfile?.organisation_name) return;
    setEventForm((prev) => (
      prev.organiser_name && prev.organiser_name !== user?.name
        ? prev  // user has typed something bespoke — respect it
        : { ...prev, organiser_name: organiserProfile.organisation_name }
    ));
    // Keep any editing event in sync too when the user opens Edit Event.
  }, [organiserProfile, user?.name]);

  const fetchRefundRequests = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/organiser/refund-requests`, { withCredentials: true });
      setRefundRequests(res.data?.refund_requests || []);
    } catch (e) {
      // non-blocking; organiser may still be setting up
    }
  };

  const decideRefund = async (refundId, action) => {
    try {
      await axios.post(`${BACKEND_URL}/api/organiser/refund-requests/${refundId}/${action}`, {}, { withCredentials: true });
      toast.success(`Refund ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchRefundRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update refund request');
    }
  };

  const handleResendVerification = async () => {
    setResendingVerify(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, {}, { withCredentials: true });
      toast.success('Verification email sent. Check your inbox.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send verification email.');
    } finally {
      setResendingVerify(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/organiser/stats`, {
        withCredentials: true,
      });
      setStats(response.data);
      setEvents(response.data.events);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be under 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setEventForm({ ...eventForm, image_url: response.data.url });
      setImagePreview(URL.createObjectURL(file));
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setImageFile(file);
      handleImageUpload(file);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      handleImageUpload(file);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/events`, eventForm, {
        withCredentials: true,
      });
      toast.success('Event created successfully!');
      setCreateDialogOpen(false);
      setEventForm({
        title: '',
        description: '',
        date: '',
        time: '',
        location: '',
        category: 'Wine Tastings',
        image_url: '',
        organiser_name: user?.name || '',
        latitude: null,
        longitude: null,
        booking_fee_strategy: 'customer_pays',
        visibility: 'public',
      });
      clearDraft();
      setImagePreview('');
      setImageFile(null);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/tickets`,
        {
          event_id: selectedEvent.event_id,
          type_name: ticketForm.type_name,
          description: ticketForm.description,
          price: ticketForm.is_free ? 0 : parseFloat(ticketForm.price || '0'),
          quantity: parseInt(ticketForm.quantity),
          age_restriction: ticketForm.age_restriction ? parseInt(ticketForm.age_restriction) : null,
        },
        { withCredentials: true }
      );
      toast.success('Ticket type created successfully!');
      setTicketDialogOpen(false);
      setTicketForm({ type_name: '', price: '', quantity: '', description: '', age_restriction: '', is_free: false });
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket type');
    }
  };

  // ===== Edit existing ticket type =====
  const [editTicketDialog, setEditTicketDialog] = useState({
    open: false,
    ticket: null,
    event: null,
    form: { type_name: '', price: '', quantity: '', description: '', age_restriction: '', is_free: false },
    submitting: false,
  });

  const openEditTicketDialog = (event, ticket) => {
    setEditTicketDialog({
      open: true,
      ticket,
      event,
      form: {
        type_name: ticket.type_name || '',
        price: ticket.price != null ? String(ticket.price) : '',
        quantity: ticket.quantity != null ? String(ticket.quantity) : '',
        description: ticket.description || '',
        age_restriction: ticket.age_restriction != null ? String(ticket.age_restriction) : '',
        is_free: Boolean(ticket.is_free || (ticket.price != null && ticket.price <= 0)),
      },
      submitting: false,
    });
  };

  const closeEditTicketDialog = () => {
    setEditTicketDialog((s) => ({ ...s, open: false, submitting: false }));
  };

  const handleSaveEditTicket = async (e) => {
    e.preventDefault();
    const { ticket, form } = editTicketDialog;
    if (!ticket) return;

    const newQty = parseInt(form.quantity, 10);
    if (!Number.isFinite(newQty) || newQty < 0) {
      toast.error('Please enter a valid quantity.');
      return;
    }
    const sold = Number(ticket.sold || 0);
    if (newQty < sold) {
      toast.error(`Quantity cannot be lower than tickets already sold (${sold}).`);
      return;
    }

    setEditTicketDialog((s) => ({ ...s, submitting: true }));
    try {
      const payload = {
        type_name: form.type_name,
        description: form.description || '',
        price: form.is_free ? 0 : parseFloat(form.price || '0'),
        quantity: newQty,
        age_restriction: form.age_restriction ? parseInt(form.age_restriction, 10) : null,
      };
      const res = await axios.put(
        `${BACKEND_URL}/api/tickets/${ticket.ticket_id}`,
        payload,
        { withCredentials: true }
      );
      const notified = res.data?.waitlist_notified || 0;
      const newlyAvailable = res.data?.newly_available_seats || 0;
      if (newlyAvailable > 0 && notified > 0) {
        toast.success(`Ticket quantity updated. ${notified} waitlisted guest${notified === 1 ? ' was' : 's were'} notified.`);
      } else if (newlyAvailable > 0) {
        toast.success('Ticket quantity updated. No waitlisted guests to notify.');
      } else {
        toast.success('Ticket quantity updated successfully.');
      }
      closeEditTicketDialog();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ticket type.');
      setEditTicketDialog((s) => ({ ...s, submitting: false }));
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/events/${eventId}`, {
        withCredentials: true,
      });
      toast.success('Event deleted successfully');
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const openCancelDialog = (event) => {
    setCancelDialog({ open: true, event, reason: '', submitting: false });
  };

  const submitCancelEvent = async () => {
    const reason = cancelDialog.reason.trim();
    if (!reason || reason.length < 10) {
      toast.error('Please provide a clear cancellation reason (at least 10 characters).');
      return;
    }
    setCancelDialog((s) => ({ ...s, submitting: true }));
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/events/${cancelDialog.event.event_id}/cancel`,
        { reason },
        { withCredentials: true },
      );
      const sent = res.data?.notifications_sent ?? 0;
      const total = res.data?.ticket_holders ?? 0;
      toast.success(
        total === 0
          ? 'Event cancelled. No ticket holders to notify.'
          : `Event cancelled. ${sent} of ${total} ticket holder${total === 1 ? '' : 's'} notified by email.`,
      );
      setCancelDialog({ open: false, event: null, reason: '', submitting: false });
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel the event.');
      setCancelDialog((s) => ({ ...s, submitting: false }));
    }
  };

  /* ----- Comp ticket dialog ----- */
  const openCompDialog = (event) => {
    const firstTicket = (event.ticket_types && event.ticket_types[0]?.ticket_id) || '';
    setCompDialog({
      open: true,
      event,
      ticket_type_id: firstTicket,
      guests: [{ name: '', email: '' }],
      message: '',
      submitting: false,
    });
  };

  const addCompGuest = () => {
    setCompDialog((s) => ({ ...s, guests: [...s.guests, { name: '', email: '' }] }));
  };
  const removeCompGuest = (idx) => {
    setCompDialog((s) => ({
      ...s,
      guests: s.guests.length === 1 ? s.guests : s.guests.filter((_, i) => i !== idx),
    }));
  };
  const updateCompGuest = (idx, field, value) => {
    setCompDialog((s) => ({
      ...s,
      guests: s.guests.map((g, i) => (i === idx ? { ...g, [field]: value } : g)),
    }));
  };

  const submitCompTickets = async () => {
    if (!compDialog.ticket_type_id) {
      toast.error('Please select a ticket type.');
      return;
    }
    const guests = compDialog.guests
      .map((g) => ({ name: (g.name || '').trim(), email: (g.email || '').trim() }))
      .filter((g) => g.name || g.email);
    if (guests.length === 0) {
      toast.error('Please add at least one guest name.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const g of guests) {
      if (!g.name) {
        toast.error('Please enter a name for every guest.');
        return;
      }
      if (!g.email) {
        toast.error('Email is required for every comp ticket guest.');
        return;
      }
      if (!emailRegex.test(g.email)) {
        toast.error(`"${g.email}" is not a valid email address.`);
        return;
      }
    }
    setCompDialog((s) => ({ ...s, submitting: true }));
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/organiser/events/${compDialog.event.event_id}/comp-tickets`,
        {
          ticket_type_id: compDialog.ticket_type_id,
          guests,
          message: compDialog.message.trim() || undefined,
        },
        { withCredentials: true },
      );
      toast.success(
        `${res.data?.quantity || guests.length} comp ticket${guests.length === 1 ? '' : 's'} issued${
          res.data?.email_sent ? ' and emailed.' : '. Email failed — please notify guests manually.'
        }`,
      );
      setCompDialog({ open: false, event: null, ticket_type_id: '', guests: [{ name: '', email: '' }], message: '', submitting: false });
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not issue comp tickets.');
      setCompDialog((s) => ({ ...s, submitting: false }));
    }
  };

  /* ----- Guest List dialog ----- */
  const openGuestList = async (event) => {
    setGuestListDialog({
      open: true, event, loading: true, rows: [], total_checked_in: 0,
      search: '', editing: null, saving: false,
    });
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/organiser/events/${event.event_id}/guests`,
        { withCredentials: true },
      );
      setGuestListDialog((s) => ({
        ...s,
        loading: false,
        rows: res.data?.guests || [],
        total_checked_in: res.data?.total_checked_in || 0,
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the guest list');
      setGuestListDialog((s) => ({ ...s, loading: false }));
    }
  };

  const startEditGuest = (row) => {
    setGuestListDialog((s) => ({
      ...s,
      editing: {
        ticket_code: row.ticket_code,
        name: row.guest_name || '',
        email: row.guest_email || '',
      },
    }));
  };

  const cancelEditGuest = () => {
    setGuestListDialog((s) => ({ ...s, editing: null }));
  };

  const saveGuestEdit = async () => {
    const edit = guestListDialog.editing;
    if (!edit) return;
    if (!edit.name.trim()) {
      toast.error('Guest name is required');
      return;
    }
    setGuestListDialog((s) => ({ ...s, saving: true }));
    try {
      const body = { name: edit.name.trim() };
      // Only send email if it's a valid-looking value or empty (to clear)
      if (edit.email !== '') body.email = edit.email.trim();
      else body.email = null;
      await axios.patch(
        `${BACKEND_URL}/api/organiser/tickets/${edit.ticket_code}/guest`,
        body,
        { withCredentials: true },
      );
      toast.success('Guest details updated');
      setGuestListDialog((s) => ({
        ...s,
        editing: null,
        saving: false,
        rows: s.rows.map((r) =>
          r.ticket_code === edit.ticket_code
            ? { ...r, guest_name: edit.name.trim(), guest_email: edit.email.trim() || null }
            : r,
        ),
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save changes');
      setGuestListDialog((s) => ({ ...s, saving: false }));
    }
  };

  const exportGuestListCSV = () => {
    const rows = guestListDialog.rows;
    if (!rows.length) return;
    const header = ['Guest Name', 'Email', 'Ticket Type', 'Ticket Code', 'Checked In', 'Type', 'Order ID'];
    const csv = [header.join(',')].concat(
      rows.map((r) => [
        `"${(r.guest_name || '').replace(/"/g, '""')}"`,
        `"${(r.guest_email || '').replace(/"/g, '""')}"`,
        `"${(r.ticket_type || '').replace(/"/g, '""')}"`,
        r.ticket_code,
        r.checked_in ? 'YES' : 'NO',
        r.is_comp ? 'COMP' : 'PAID',
        r.order_id,
      ].join(',')),
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `VinePass_Guests_${guestListDialog.event?.title?.replace(/[^a-z0-9]/gi, '_') || 'event'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ----- Individual Ticket Cancellation ----- */
  const openCancelTicket = (row) => {
    const isFreeOrComp = row.is_comp || !row.ticket_code; // we can't know price client-side; default refund true for paid
    setCancelTicketDialog({
      open: true,
      row,
      refund: !isFreeOrComp,
      reason: '',
      submitting: false,
    });
  };

  const submitCancelTicket = async () => {
    const row = cancelTicketDialog.row;
    if (!row) return;
    setCancelTicketDialog((s) => ({ ...s, submitting: true }));
    try {
      await axios.post(
        `${BACKEND_URL}/api/organiser/tickets/${row.ticket_code}/cancel`,
        { refund: cancelTicketDialog.refund, reason: cancelTicketDialog.reason.trim() || undefined },
        { withCredentials: true },
      );
      toast.success(
        cancelTicketDialog.refund
          ? 'Ticket cancelled and refund recorded. Balance has been adjusted.'
          : 'Ticket cancelled.',
      );
      // Update local guest list state
      setGuestListDialog((s) => ({
        ...s,
        rows: s.rows.map((r) =>
          r.ticket_code === row.ticket_code
            ? {
                ...r,
                cancelled: true,
                refund_status: cancelTicketDialog.refund ? 'approved' : 'not_applicable',
              }
            : r,
        ),
      }));
      setCancelTicketDialog({ open: false, row: null, refund: true, reason: '', submitting: false });
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel the ticket.');
      setCancelTicketDialog((s) => ({ ...s, submitting: false }));
    }
  };

  const openEditDialog = (event) => {
    setEditingEvent({
      event_id: event.event_id,
      title: event.title || '',
      description: event.description || '',
      date: event.date || '',
      time: event.time || '',
      location: event.location || '',
      category: event.category || 'Wine Tastings',
      image_url: event.image_url || '',
      booking_fee_strategy: event.booking_fee_strategy || 'customer_pays',
      visibility: event.visibility || 'public',
    });
    setEditDialogOpen(true);
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!editingEvent) return;
    const { event_id, ...payload } = editingEvent;
    try {
      await axios.put(`${BACKEND_URL}/api/events/${event_id}`, payload, {
        withCredentials: true,
      });
      toast.success('Event updated successfully!');
      setEditDialogOpen(false);
      setEditingEvent(null);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    }
  };

  const handleEditImageUpload = async (file) => {
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be under 5MB');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditingEvent({ ...editingEvent, image_url: response.data.url });
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Image upload failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#4A5D50]">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1
                className="text-4xl sm:text-5xl font-bold text-[#112318]"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="dashboard-title"
              >
                Organiser Dashboard
              </h1>
              <DemoModeBadge />
            </div>
            <p className="text-[#4A5D50]">Welcome back, {user?.name}</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => setProfileFormOpen(true)}
              variant="outline"
              className="rounded-full px-6 py-3 border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#F4F6F3]"
              data-testid="manage-profile-button"
            >
              <Building2 className="w-5 h-5 mr-2" />
              {organiserProfile ? 'Edit Profile' : 'Set up Profile'}
            </Button>

            <Button
              onClick={() => window.location.href = '/scanner'}
              variant="outline"
              className="rounded-full px-6 py-3 border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#F4F6F3]"
              data-testid="scan-tickets-button"
            >
              <ScanLine className="w-5 h-5 mr-2" />
              Scan Tickets
            </Button>

            <Dialog
              open={createDialogOpen}
              onOpenChange={(open) => {
                if (open) {
                  if (user && !user.email_verified && user.role !== 'admin') {
                    toast.info('Please verify your email first.');
                    return;
                  }
                  if (!organiserProfile && user?.role !== 'admin') {
                    toast.info('Please complete your organiser profile first.');
                    setProfileFormOpen(true);
                    return;
                  }
                }
                setCreateDialogOpen(open);
              }}
            >
              <DialogTrigger asChild>
                <Button
                  className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-6 py-3"
                  data-testid="create-event-button"
                  disabled={user && !user.email_verified && user.role !== 'admin'}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Event
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Event</DialogTitle>
              </DialogHeader>
              {hasDraft && (
                <div
                  className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                  data-testid="event-draft-restored"
                >
                  <span>We restored your unsaved event draft.</span>
                  <button
                    type="button"
                    onClick={() => {
                      setEventForm({
                        title: '',
                        description: '',
                        date: '',
                        time: '',
                        location: '',
                        category: 'Wine Tastings',
                        image_url: '',
                        organiser_name: user?.name || '',
                        latitude: null,
                        longitude: null,
                        booking_fee_strategy: 'customer_pays',
                        visibility: 'public',
                      });
                      clearDraft();
                    }}
                    className="font-medium underline hover:no-underline"
                    data-testid="event-draft-discard"
                  >
                    Discard draft
                  </button>
                </div>
              )}
              <form onSubmit={handleCreateEvent} className="space-y-4">
                <div>
                  <Label htmlFor="title">Event Title</Label>
                  <Input
                    id="title"
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    required
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={eventForm.date}
                      onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="time">Time</Label>
                    <Input
                      id="time"
                      type="time"
                      value={eventForm.time}
                      onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={eventForm.location}
                    onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                    placeholder="e.g., Stellenbosch Wine Estate"
                    required
                  />
                </div>
                <div>
                  <Label>Pin on the map (optional)</Label>
                  <LocationPicker
                    value={{ lat: eventForm.latitude, lng: eventForm.longitude }}
                    onChange={(p) => setEventForm({ ...eventForm, latitude: p?.lat ?? null, longitude: p?.lng ?? null })}
                    height={260}
                  />
                  <p className="text-xs text-[#4A5D50] mt-1">Drop a pin so attendees can navigate to your venue.</p>
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <select
                    id="category"
                    value={eventForm.category}
                    onChange={(e) => setEventForm({ ...eventForm, category: e.target.value })}
                    className="w-full border border-[#E5E9E6] rounded-lg p-2"
                    required
                  >
                    <option value="Wine Tastings">Wine Tastings</option>
                    <option value="Food & Pairings">Food & Pairings</option>
                    <option value="Live Music">Live Music</option>
                    <option value="Markets & Pop-Ups">Markets & Pop-Ups</option>
                    <option value="Lifestyle Events">Lifestyle Events</option>
                    <option value="Special Events">Special Events</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="booking-fee-strategy">Booking Fee Strategy</Label>
                  <select
                    id="booking-fee-strategy"
                    value={eventForm.booking_fee_strategy}
                    onChange={(e) => setEventForm({ ...eventForm, booking_fee_strategy: e.target.value })}
                    className="w-full border border-[#E5E9E6] rounded-lg p-2"
                    data-testid="create-fee-strategy"
                  >
                    <option value="customer_pays">Customer pays the booking fee</option>
                    <option value="organiser_absorbs">I absorb the booking fee</option>
                    <option value="split_50_50">Split 50/50 with the customer</option>
                  </select>
                  <p className="text-[11px] text-[#4A5D50] mt-1 leading-relaxed">
                    VinePass always receives the full curation fee. Any portion you absorb is deducted from your payout. Free + comp tickets are always fee-free.
                  </p>
                </div>
                <div>
                  <Label htmlFor="create-visibility">Event Visibility</Label>
                  <select
                    id="create-visibility"
                    value={eventForm.visibility}
                    onChange={(e) => setEventForm({ ...eventForm, visibility: e.target.value })}
                    className="w-full border border-[#E5E9E6] rounded-lg p-2"
                    data-testid="create-visibility"
                  >
                    <option value="public">Public Event — visible on VinePass listings</option>
                    <option value="unlisted">Unlisted / Private Link Event — direct link only</option>
                  </select>
                  <p className="text-[11px] text-[#4A5D50] mt-1 leading-relaxed">
                    Unlisted events are hidden from public listings, search and filters. Only people with the direct event link can view and book them.
                  </p>
                </div>
                <div>
                  <Label htmlFor="image_url">Event Image</Label>
                  <div className="space-y-3">
                    {/* Drag & Drop Zone */}
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={handleImageDrop}
                      className="border-2 border-dashed border-[#E5E9E6] rounded-lg p-6 text-center hover:border-[#4E7C59] transition-colors cursor-pointer"
                      onClick={() => document.getElementById('image-upload').click()}
                    >
                      {uploadingImage ? (
                        <div className="py-4">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3D2B] mx-auto mb-2"></div>
                          <p className="text-sm text-[#4A5D50]">Uploading...</p>
                        </div>
                      ) : imagePreview ? (
                        <div className="space-y-2">
                          <img src={imagePreview} alt="Preview" className="max-h-32 mx-auto rounded" />
                          <p className="text-xs text-[#4E7C59]">✓ Image uploaded</p>
                        </div>
                      ) : (
                        <div className="py-4">
                          <svg className="mx-auto h-12 w-12 text-[#4A5D50]" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <p className="mt-2 text-sm text-[#4A5D50]">
                            <span className="font-semibold text-[#1F3D2B]">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-[#4A5D50] mt-1">PNG, JPG, WebP up to 5MB</p>
                        </div>
                      )}
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                    </div>

                    {/* OR divider — only shown when image hasn't been uploaded internally */}
                    {!eventForm.image_url?.startsWith('/api/files/') && (
                      <>
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-[#E5E9E6]"></div>
                          </div>
                          <div className="relative flex justify-center text-xs">
                            <span className="px-2 bg-white text-[#4A5D50]">OR</span>
                          </div>
                        </div>

                        {/* URL Input — only for external image URLs */}
                        <div>
                          <Input
                            id="image_url"
                            type="url"
                            value={eventForm.image_url}
                            onChange={(e) => {
                              setEventForm({ ...eventForm, image_url: e.target.value });
                              setImagePreview('');
                            }}
                            placeholder="https://example.com/image.jpg"
                            disabled={uploadingImage}
                          />
                          <p className="text-xs text-[#4A5D50] mt-1">Or paste an image URL</p>
                        </div>
                      </>
                    )}

                    {/* Replace-image control when an internal upload exists */}
                    {eventForm.image_url?.startsWith('/api/files/') && (
                      <button
                        type="button"
                        onClick={() => {
                          setEventForm({ ...eventForm, image_url: '' });
                          setImagePreview('');
                        }}
                        className="text-xs text-[#4A5D50] hover:text-[#1F3D2B] underline"
                        data-testid="replace-image-button"
                      >
                        Remove / replace image
                      </button>
                    )}
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full"
                >
                  Create Event
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* First-session onboarding checklist (also handles verify-email nudge) */}
        <OnboardingChecklist
          user={user}
          hasFirstEvent={(events?.length || 0) > 0}
          hasProfile={!!organiserProfile}
          hasBanking={!!banking}
          onResendVerification={handleResendVerification}
          onSetupProfile={() => setProfileFormOpen(true)}
          onAddBanking={() => {
            const el = document.querySelector('[data-testid="payouts-section"]');
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              // give the panel a moment to be in view, then auto-open the edit form
              setTimeout(() => {
                const editBtn = document.querySelector('[data-testid="bank-edit"]');
                if (editBtn) editBtn.click();
              }, 400);
            }
          }}
          resending={resendingVerify}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-testid="stats-cards">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E9E6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#4A5D50] text-sm">Total Events</p>
                <p className="text-3xl font-bold text-[#112318]" data-testid="total-events">
                  {stats?.total_events || 0}
                </p>
              </div>
              <Calendar className="w-12 h-12 text-[#4E7C59]" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E9E6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#4A5D50] text-sm">Tickets Sold</p>
                <p className="text-3xl font-bold text-[#112318]" data-testid="total-tickets-sold">
                  {stats?.total_tickets_sold || 0}
                </p>
              </div>
              <Ticket className="w-12 h-12 text-[#4E7C59]" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E9E6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#4A5D50] text-sm">Total Revenue</p>
                <p className="text-3xl font-bold text-[#112318]" data-testid="total-revenue">
                  R{stats?.total_revenue?.toFixed(2) || '0.00'}
                </p>
              </div>
              <DollarSign className="w-12 h-12 text-[#4E7C59]" />
            </div>
          </div>
        </div>

        {/* Events List */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E5E9E6]">
          <div className="p-6 border-b border-[#E5E9E6] flex items-center justify-between gap-3 flex-wrap">
            <h2
              className="text-2xl font-semibold text-[#112318]"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Your Events
            </h2>
            {events.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { window.location.href = `${BACKEND_URL}/api/organiser/exports/all-guests.csv`; }}
                  className="rounded-full"
                  data-testid="export-all-guests-csv"
                >
                  Export all guests (CSV)
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { window.location.href = `${BACKEND_URL}/api/organiser/exports/all-orders.csv`; }}
                  className="rounded-full"
                  data-testid="export-all-orders-csv"
                >
                  Export all orders (CSV)
                </Button>
              </div>
            )}
          </div>

          {events.length === 0 ? (
            <div className="p-12 text-center text-[#4A5D50]">
              <p className="mb-4">You haven't created any events yet.</p>
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="bg-[#1F3D2B] text-white rounded-full"
              >
                Create Your First Event
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-[#E5E9E6]" data-testid="events-list">
              {events.map((event) => (
                <div key={event.event_id} className="p-6 hover:bg-[#FAFAF7] transition-colors">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-xl font-semibold text-[#112318]">{event.title}</h3>
                        {event.status === 'cancelled' && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-rose-50 text-rose-800 border border-rose-200"
                            data-testid={`event-cancelled-badge-${event.event_id}`}
                          >
                            <Ban className="w-3 h-3" /> Cancelled
                          </span>
                        )}
                        {event.admin_moderation && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                            data-testid={`event-moderation-badge-${event.event_id}`}
                            title={event.admin_moderation.reason || ''}
                          >
                            {(event.status || '').replace('_', ' ')} by admin
                          </span>
                        )}
                        {event.visibility === 'unlisted' && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-stone-100 text-stone-700 border border-stone-200"
                            data-testid={`event-unlisted-badge-${event.event_id}`}
                            title="Hidden from public listings. Bookable by direct link only."
                          >
                            <Link2 className="w-3 h-3" /> Unlisted
                          </span>
                        )}
                      </div>
                      {event.admin_moderation && (
                        <div
                          className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 leading-relaxed"
                          data-testid={`event-moderation-note-${event.event_id}`}
                        >
                          <p className="font-medium mb-0.5">
                            Your event has been placed under VinePass moderation. Ticket sales are currently paused.
                          </p>
                          <p>
                            <span className="font-medium">Reason:</span> {event.admin_moderation.reason || '—'}
                          </p>
                          <p className="text-amber-800/80 mt-1">
                            Please contact <a className="underline" href="mailto:admin@vinepass.co.za">admin@vinepass.co.za</a> for more information.
                          </p>
                        </div>
                      )}
                      <p className="text-[#4A5D50] text-sm mb-2">{event.description}</p>
                      <div className="flex gap-4 text-sm text-[#4A5D50] mb-3">
                        <span>{event.date} at {event.time}</span>
                        <span>•</span>
                        <span>{event.location}</span>
                        <span>•</span>
                        <span className="text-[#4E7C59]">{event.category}</span>
                      </div>

                      {/* Per-event sales stats */}
                      <div className="flex flex-wrap gap-2 mt-2" data-testid={`event-stats-${event.event_id}`}>
                        <span className="text-xs bg-[#F4F6F3] text-[#1F3D2B] px-3 py-1 rounded-full border border-[#E5E9E6]">
                          <span className="font-semibold">{event.tickets_sold || 0}</span>
                          <span className="text-[#4A5D50]"> / {event.capacity || 0}</span>
                          <span className="ml-1 text-[#4A5D50]">sold</span>
                        </span>
                        <span className="text-xs bg-[#F4F6F3] text-[#1F3D2B] px-3 py-1 rounded-full border border-[#E5E9E6]">
                          <span className="text-[#4A5D50]">Revenue:</span>
                          <span className="font-semibold ml-1">R{(event.revenue || 0).toFixed(2)}</span>
                        </span>
                        {event.ticket_types && event.ticket_types.length > 0 && (
                          <span className="text-xs bg-[#F4F6F3] text-[#4A5D50] px-3 py-1 rounded-full border border-[#E5E9E6]">
                            {event.ticket_types.length} ticket type{event.ticket_types.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      {/* Per-ticket-type rows with inline edit */}
                      {event.ticket_types && event.ticket_types.length > 0 && (
                        <div className="mt-3 divide-y divide-stone-100 border border-stone-100 rounded-lg" data-testid={`ticket-types-${event.event_id}`}>
                          {event.ticket_types.map((tt) => {
                            const sold = Number(tt.sold || 0);
                            const qty = Number(tt.quantity || 0);
                            const soldOut = qty - sold <= 0;
                            return (
                              <div key={tt.ticket_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`ticket-type-row-${tt.ticket_id}`}>
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-[#112318] truncate" data-testid={`ticket-row-name-${tt.ticket_id}`}>
                                    {tt.type_name}
                                  </p>
                                  <p className="text-xs text-[#4A5D50]">
                                    {tt.is_free || tt.price <= 0 ? 'Free' : `R${Number(tt.price).toFixed(2)}`} · {sold} / {qty} sold
                                    {soldOut && (
                                      <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200">
                                        Sold out
                                      </span>
                                    )}
                                  </p>
                                </div>
                                {event.status !== 'cancelled' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openEditTicketDialog(event, tt)}
                                    className="text-[#1F3D2B] hover:bg-[#F4F6F3]"
                                    aria-label="Edit ticket type"
                                    title="Edit ticket type"
                                    data-testid={`edit-ticket-button-${tt.ticket_id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        data-testid={`copy-link-button-${event.event_id}`}
                        onClick={async () => {
                          const url = `${window.location.origin}/events/${event.event_id}`;
                          try {
                            if (navigator.clipboard?.writeText) {
                              await navigator.clipboard.writeText(url);
                            } else {
                              // Older Safari fallback — temporary textarea + execCommand.
                              const ta = document.createElement('textarea');
                              ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
                              document.body.appendChild(ta); ta.select();
                              document.execCommand('copy'); document.body.removeChild(ta);
                            }
                            toast.success(
                              event.visibility === 'unlisted'
                                ? 'Private link copied — only people with this link can view this event.'
                                : 'Event link copied.',
                            );
                          } catch {
                            toast.error('Could not copy link. Please copy it manually from the address bar.');
                          }
                        }}
                        title={event.visibility === 'unlisted' ? 'Copy private event link' : 'Copy event link'}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy link
                      </Button>
                      <Dialog
                        open={ticketDialogOpen && selectedEvent?.event_id === event.event_id}
                        onOpenChange={(open) => {
                          setTicketDialogOpen(open);
                          if (open) setSelectedEvent(event);
                        }}
                      >
                        <DialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full"
                            data-testid={`add-ticket-button-${event.event_id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Ticket
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Create Ticket Type</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleCreateTicket} className="space-y-4">
                            <div>
                              <Label htmlFor="type_name">Ticket Name</Label>
                              <Input
                                id="type_name"
                                value={ticketForm.type_name}
                                onChange={(e) => setTicketForm({ ...ticketForm, type_name: e.target.value })}
                                placeholder="e.g., General Admission, VIP"
                                required
                              />
                            </div>

                            {/* Free / Paid toggle */}
                            <label className="flex items-center gap-3 p-3 border border-[#E5E9E6] rounded-lg cursor-pointer hover:bg-[#FAFAF7]">
                              <input
                                type="checkbox"
                                checked={ticketForm.is_free}
                                onChange={(e) => setTicketForm({ ...ticketForm, is_free: e.target.checked, price: e.target.checked ? '0' : ticketForm.price })}
                                data-testid="ticket-is-free"
                                className="w-4 h-4"
                              />
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-[#1F3D2B]">Free ticket (R0)</p>
                                <p className="text-xs text-[#4A5D50]">No booking fee charged for free tickets.</p>
                              </div>
                            </label>

                            <div className="grid grid-cols-2 gap-4">
                              {!ticketForm.is_free && (
                                <div>
                                  <Label htmlFor="price">Price (ZAR)</Label>
                                  <Input
                                    id="price"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={ticketForm.price}
                                    onChange={(e) => setTicketForm({ ...ticketForm, price: e.target.value })}
                                    required
                                  />
                                </div>
                              )}
                              <div className={ticketForm.is_free ? 'col-span-2' : ''}>
                                <Label htmlFor="quantity">Quantity</Label>
                                <Input
                                  id="quantity"
                                  type="number"
                                  min="1"
                                  value={ticketForm.quantity}
                                  onChange={(e) => setTicketForm({ ...ticketForm, quantity: e.target.value })}
                                  required
                                />
                              </div>
                            </div>

                            <div>
                              <Label htmlFor="age_restriction">Age Restriction (Optional)</Label>
                              <select
                                id="age_restriction"
                                data-testid="ticket-age-restriction"
                                value={ticketForm.age_restriction}
                                onChange={(e) => setTicketForm({ ...ticketForm, age_restriction: e.target.value })}
                                className="w-full border border-[#E5E9E6] rounded-lg p-2"
                              >
                                <option value="">No age restriction</option>
                                <option value="13">13+</option>
                                <option value="16">16+</option>
                                <option value="18">18+ (recommended for wine events)</option>
                                <option value="21">21+</option>
                              </select>
                              <p className="text-xs text-[#4A5D50] mt-1">Shown on event listings; not enforced at checkout.</p>
                            </div>

                            <div>
                              <Label htmlFor="description">Description (Optional)</Label>
                              <Textarea
                                id="description"
                                value={ticketForm.description}
                                onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                                rows={2}
                              />
                            </div>
                            <Button
                              type="submit"
                              className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full"
                            >
                              Create Ticket Type
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(event)}
                        className="text-[#1F3D2B] hover:bg-[#F4F6F3]"
                        aria-label="Edit event"
                        title="Edit event"
                        data-testid={`edit-event-button-${event.event_id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openGuestList(event)}
                        className="text-[#1F3D2B] hover:bg-[#F4F6F3]"
                        aria-label="View guest list"
                        title="View guest list"
                        data-testid={`guest-list-button-${event.event_id}`}
                      >
                        <Users className="w-4 h-4" />
                      </Button>
                      {event.ticket_types && event.ticket_types.length > 0 && event.status !== 'cancelled' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openCompDialog(event)}
                          className="text-[#C9A27A] hover:bg-[#C9A27A]/10"
                          aria-label="Send comp tickets"
                          title="Send complimentary tickets"
                          data-testid={`comp-ticket-button-${event.event_id}`}
                        >
                          <Gift className="w-4 h-4" />
                        </Button>
                      )}
                      {event.status !== 'cancelled' && event.status !== 'deleted' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openCancelDialog(event)}
                          className="text-[#B8862B] hover:bg-[#B8862B]/10"
                          aria-label="Cancel event"
                          title="Cancel event"
                          data-testid={`cancel-event-button-${event.event_id}`}
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteEvent(event.event_id)}
                        className="text-[#B04D4D] hover:bg-[#B04D4D]/10"
                        data-testid={`delete-event-button-${event.event_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Refund Requests */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E5E9E6] mt-8" data-testid="refund-requests-section">
          <div className="p-6 border-b border-[#E5E9E6] flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-[#112318]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Refund Requests
            </h2>
            <span className="text-xs uppercase tracking-widest text-[#4A5D50]">
              {refundRequests.filter(r => r.status === 'pending' || r.status === 'pending_organiser_review').length} pending
            </span>
          </div>
          {refundRequests.length === 0 ? (
            <div className="p-12 text-center text-[#4A5D50] text-sm">
              No refund requests yet.
            </div>
          ) : (
            <div className="divide-y divide-[#E5E9E6]">
              {refundRequests.map((r) => {
                // Coerce legacy → new statuses for display
                const status = r.status === 'pending' ? 'pending_organiser_review'
                             : r.status === 'approved' ? 'pending_admin_processing'
                             : r.status === 'rejected' ? 'rejected_by_organiser'
                             : r.status;
                const isPending = status === 'pending_organiser_review';
                const STATUS_TONES = {
                  pending_organiser_review: 'bg-amber-50 text-amber-800 border-amber-200',
                  rejected_by_organiser:    'bg-rose-50 text-rose-800 border-rose-200',
                  pending_admin_processing: 'bg-violet-50 text-violet-800 border-violet-200',
                  processing_payfast:       'bg-indigo-50 text-indigo-800 border-indigo-200',
                  refunded_payfast:         'bg-emerald-50 text-emerald-800 border-emerald-200',
                  manual_eft_required:      'bg-orange-50 text-orange-800 border-orange-200',
                  manual_eft_paid:          'bg-emerald-50 text-emerald-800 border-emerald-200',
                  failed:                   'bg-rose-50 text-rose-800 border-rose-200',
                  cancelled:                'bg-stone-100 text-stone-700 border-stone-200',
                };
                const STATUS_LABEL = {
                  pending_organiser_review: 'Pending Review',
                  rejected_by_organiser:    'Rejected',
                  pending_admin_processing: 'Awaiting VinePass',
                  processing_payfast:       'Processing (PayFast)',
                  refunded_payfast:         'Refunded',
                  manual_eft_required:      'Manual EFT Pending',
                  manual_eft_paid:          'EFT Paid',
                  failed:                   'Failed',
                  cancelled:                'Cancelled',
                };
                return (
                <div key={r.refund_id} className="p-6 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4" data-testid={`refund-row-${status}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider rounded-full border ${STATUS_TONES[status] || 'bg-stone-100 text-stone-700 border-stone-200'}`}>
                        {STATUS_LABEL[status] || status}
                      </span>
                      <p className="font-semibold text-[#112318] truncate">{r.event_title || 'Event'}</p>
                    </div>
                    <p className="text-sm text-[#4A5D50] mb-2">
                      {r.customer_name} · {r.customer_email} · R{(r.approved_refund_amount ?? r.amount ?? 0).toFixed(2)}
                    </p>
                    <p className="text-sm text-stone-600 italic">"{r.reason}"</p>
                    <p className="text-xs text-stone-400 mt-2">
                      Requested {new Date(r.requested_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {status !== 'pending_organiser_review' && status !== 'rejected_by_organiser' && (
                        <span className="text-[10px] uppercase tracking-wider text-stone-400 ml-2">· VinePass processes payment</span>
                      )}
                    </p>
                  </div>
                  {isPending && (
                    <div className="flex gap-2 lg:flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => decideRefund(r.refund_id, 'reject')}
                        className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50"
                        data-testid={`reject-refund-${r.refund_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => decideRefund(r.refund_id, 'approve')}
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`approve-refund-${r.refund_id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Owner-only: Team + Payouts */}
        {user?.role === 'organiser' && (
          <>
            <TeamSection events={events} />
            <PayoutsSection onBankingSaved={fetchBanking} />

            {/* Organiser-facing compliance & policy links */}
            <div
              className="mt-8 bg-white border border-[#E5E9E6] rounded-xl p-6"
              data-testid="organiser-compliance-block"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-[#C9A27A] mb-3">
                Organiser Trust &amp; Policies
              </p>
              <p className="text-sm text-[#4A5D50] leading-relaxed mb-4">
                VinePass is operated by <span className="font-semibold text-[#1F3D2B]">VINEPASS (Pty) Ltd</span>,
                a South African registered company.<br />
                Reg No: <span className="font-semibold text-[#1F3D2B]">2026/412801/07</span>
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
                <Link to="/organiser-terms" className="text-[#1F3D2B] underline hover:no-underline">Organiser Terms</Link>
                <Link to="/payout-policy" className="text-[#1F3D2B] underline hover:no-underline">Payout Policy</Link>
                <Link to="/refund-policy" className="text-[#1F3D2B] underline hover:no-underline">Refund Policy</Link>
                <Link to="/privacy" className="text-[#1F3D2B] underline hover:no-underline">Privacy Policy</Link>
                <Link to="/terms" className="text-[#1F3D2B] underline hover:no-underline">Terms &amp; Conditions</Link>
              </div>
            </div>
          </>
        )}

        {/* Edit Event Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setEditingEvent(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Event</DialogTitle>
            </DialogHeader>
            {editingEvent && (
              <form onSubmit={handleUpdateEvent} className="space-y-4" data-testid="edit-event-form">
                <div>
                  <Label htmlFor="edit-title">Event Title</Label>
                  <Input
                    id="edit-title"
                    value={editingEvent.title}
                    onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })}
                    required
                    data-testid="edit-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editingEvent.description}
                    onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                    required
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-date">Date</Label>
                    <Input
                      id="edit-date"
                      type="date"
                      value={editingEvent.date}
                      onChange={(e) => setEditingEvent({ ...editingEvent, date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-time">Time</Label>
                    <Input
                      id="edit-time"
                      type="time"
                      value={editingEvent.time}
                      onChange={(e) => setEditingEvent({ ...editingEvent, time: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-location">Location</Label>
                  <Input
                    id="edit-location"
                    value={editingEvent.location}
                    onChange={(e) => setEditingEvent({ ...editingEvent, location: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit-category">Category</Label>
                  <select
                    id="edit-category"
                    value={editingEvent.category}
                    onChange={(e) => setEditingEvent({ ...editingEvent, category: e.target.value })}
                    className="w-full border border-[#E5E9E6] rounded-lg p-2"
                    required
                  >
                    <option value="Wine Tastings">Wine Tastings</option>
                    <option value="Food & Pairings">Food & Pairings</option>
                    <option value="Live Music">Live Music</option>
                    <option value="Markets & Pop-Ups">Markets & Pop-Ups</option>
                    <option value="Lifestyle Events">Lifestyle Events</option>
                    <option value="Special Events">Special Events</option>
                    {/* Legacy categories — only offered while the event still
                        uses one, so the dropdown round-trips cleanly without
                        forcing a migration. */}
                    {editingEvent.category === 'Outdoor Picnics' && (
                      <option value="Outdoor Picnics">Outdoor Picnics (legacy)</option>
                    )}
                    {editingEvent.category === 'Activities' && (
                      <option value="Activities">Activities (legacy)</option>
                    )}
                  </select>
                </div>
                <div>
                  <Label htmlFor="edit-booking-fee-strategy">Booking Fee Strategy</Label>
                  <select
                    id="edit-booking-fee-strategy"
                    value={editingEvent.booking_fee_strategy || 'customer_pays'}
                    onChange={(e) => setEditingEvent({ ...editingEvent, booking_fee_strategy: e.target.value })}
                    className="w-full border border-[#E5E9E6] rounded-lg p-2"
                    data-testid="edit-fee-strategy"
                  >
                    <option value="customer_pays">Customer pays the booking fee</option>
                    <option value="organiser_absorbs">I absorb the booking fee</option>
                    <option value="split_50_50">Split 50/50 with the customer</option>
                  </select>
                  <p className="text-[11px] text-[#4A5D50] mt-1 leading-relaxed">
                    Applies to NEW orders only — orders already placed keep their original strategy.
                  </p>
                </div>
                <div>
                  <Label htmlFor="edit-visibility">Event Visibility</Label>
                  <select
                    id="edit-visibility"
                    value={editingEvent.visibility || 'public'}
                    onChange={(e) => setEditingEvent({ ...editingEvent, visibility: e.target.value })}
                    className="w-full border border-[#E5E9E6] rounded-lg p-2"
                    data-testid="edit-visibility"
                  >
                    <option value="public">Public Event — visible on VinePass listings</option>
                    <option value="unlisted">Unlisted / Private Link Event — direct link only</option>
                  </select>
                  <p className="text-[11px] text-[#4A5D50] mt-1 leading-relaxed">
                    Unlisted events are hidden from public listings, search and filters. Anyone with the direct link can still view and book.
                  </p>
                </div>
                <div>
                  <Label>Event Image</Label>
                  {editingEvent.image_url && (
                    <img src={editingEvent.image_url} alt="Current" className="max-h-32 rounded mb-2" />
                  )}
                  <Input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    onChange={(e) => handleEditImageUpload(e.target.files[0])}
                    className="mb-2"
                  />
                  {/* External URL input — only when image is not an internal upload */}
                  {!editingEvent.image_url?.startsWith('/api/files/') && (
                    <Input
                      type="url"
                      value={editingEvent.image_url}
                      onChange={(e) => setEditingEvent({ ...editingEvent, image_url: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                    />
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full"
                  data-testid="save-edit-event-button"
                >
                  Save Changes
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Organiser Profile Modal */}
        {profileFormOpen && (
          <OrganiserProfileForm
            existing={organiserProfile}
            onSaved={(p) => { setOrganiserProfile(p); setProfileFormOpen(false); }}
            onClose={() => setProfileFormOpen(false)}
          />
        )}

        {/* Cancel Event Confirmation Modal */}
        <Dialog
          open={cancelDialog.open}
          onOpenChange={(open) => !open && setCancelDialog({ open: false, event: null, reason: '', submitting: false })}
        >
          <DialogContent data-testid="cancel-event-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#1F3D2B] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" /> Cancel this event?
              </DialogTitle>
            </DialogHeader>
            {cancelDialog.event && (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                  <p className="font-medium mb-1">{cancelDialog.event.title}</p>
                  <p className="text-xs text-amber-800">
                    {cancelDialog.event.tickets_sold || 0} ticket{(cancelDialog.event.tickets_sold || 0) === 1 ? '' : 's'} sold ·
                    R{(cancelDialog.event.revenue || 0).toFixed(2)} revenue
                  </p>
                </div>
                <p className="text-sm text-stone-700 leading-relaxed">
                  Cancelling will <strong>disable further ticket sales</strong> and <strong>email every ticket holder</strong>
                  with the reason and refund information. This cannot be undone.
                </p>
                <div>
                  <Label htmlFor="cancel-reason" className="text-[#4A5D50]">Reason (shared with ticket holders)</Label>
                  <Textarea
                    id="cancel-reason"
                    placeholder="e.g. Venue flooded due to storm damage. We are deeply sorry for any inconvenience."
                    value={cancelDialog.reason}
                    onChange={(e) => setCancelDialog((s) => ({ ...s, reason: e.target.value }))}
                    rows={4}
                    className="mt-1"
                    data-testid="cancel-event-reason"
                    maxLength={600}
                  />
                  <p className="text-[11px] text-stone-500 mt-1">
                    {cancelDialog.reason.length}/600 characters · at least 10 required
                  </p>
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <Button
                    variant="ghost"
                    onClick={() => setCancelDialog({ open: false, event: null, reason: '', submitting: false })}
                    disabled={cancelDialog.submitting}
                  >
                    Keep event
                  </Button>
                  <Button
                    onClick={submitCancelEvent}
                    disabled={cancelDialog.submitting || cancelDialog.reason.trim().length < 10}
                    className="bg-rose-600 text-white hover:bg-rose-700 rounded-full"
                    data-testid="cancel-event-confirm"
                  >
                    {cancelDialog.submitting ? 'Cancelling…' : 'Yes, cancel event'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Comp Tickets Dialog */}
        <Dialog
          open={compDialog.open}
          onOpenChange={(open) => !open && setCompDialog({ open: false, event: null, ticket_type_id: '', guests: [{ name: '', email: '' }], message: '', submitting: false })}
        >
          <DialogContent className="max-w-lg" data-testid="comp-ticket-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#1F3D2B] flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#C9A27A]" /> Send Complimentary Tickets
              </DialogTitle>
              <DialogDescription className="sr-only">
                Issue complimentary tickets to guests without checkout. Each guest receives an email with their QR code.
              </DialogDescription>
            </DialogHeader>
            {compDialog.event && (
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                <div className="bg-[#F4F6F3] border border-[#E5E9E6] rounded-lg p-3 text-sm">
                  <p className="font-medium text-[#1F3D2B]">{compDialog.event.title}</p>
                  <p className="text-xs text-[#4A5D50] mt-1">
                    Comp tickets are issued at <strong>R0</strong>, with no booking fee and no refund eligibility.
                  </p>
                </div>

                <div>
                  <Label className="text-[#4A5D50]">Ticket Type</Label>
                  <select
                    value={compDialog.ticket_type_id}
                    onChange={(e) => setCompDialog((s) => ({ ...s, ticket_type_id: e.target.value }))}
                    className="w-full border border-[#E5E9E6] rounded-lg p-2 mt-1 bg-white"
                    data-testid="comp-ticket-type-select"
                  >
                    {(compDialog.event.ticket_types || []).map((tt) => (
                      <option key={tt.ticket_id} value={tt.ticket_id}>
                        {`${tt.type_name} · ${tt.quantity - tt.sold} available`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[#4A5D50]">Guests</Label>
                    <Button type="button" size="sm" variant="ghost" onClick={addCompGuest} data-testid="comp-add-guest-button">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add guest
                    </Button>
                  </div>
                  <div className="space-y-2" data-testid="comp-guest-list">
                    {compDialog.guests.map((g, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr,1fr,auto] gap-2 items-center">
                        <Input
                          type="text"
                          placeholder="Guest name *"
                          value={g.name}
                          onChange={(e) => updateCompGuest(idx, 'name', e.target.value)}
                          data-testid={`comp-guest-name-${idx}`}
                        />
                        <Input
                          type="email"
                          placeholder="Email *"
                          required
                          value={g.email}
                          onChange={(e) => updateCompGuest(idx, 'email', e.target.value)}
                          data-testid={`comp-guest-email-${idx}`}
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => removeCompGuest(idx)}
                          disabled={compDialog.guests.length === 1}
                          aria-label="Remove guest"
                          data-testid={`comp-guest-remove-${idx}`}
                        >
                          <Trash2 className="w-4 h-4 text-stone-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-stone-500 mt-2">
                    Every guest needs a valid email so we can confirm delivery of their ticket. The
                    first guest's email receives the confirmation with all PDF tickets attached.
                  </p>
                </div>

                <div>
                  <Label className="text-[#4A5D50]">Personal note (optional)</Label>
                  <Textarea
                    rows={2}
                    maxLength={500}
                    placeholder="Thanks for joining us — looking forward to seeing you!"
                    value={compDialog.message}
                    onChange={(e) => setCompDialog((s) => ({ ...s, message: e.target.value }))}
                    className="mt-1"
                    data-testid="comp-message"
                  />
                </div>

                <div className="flex gap-2 justify-end pt-2">
                  <Button
                    variant="ghost"
                    onClick={() => setCompDialog({ open: false, event: null, ticket_type_id: '', guests: [{ name: '', email: '' }], message: '', submitting: false })}
                    disabled={compDialog.submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submitCompTickets}
                    disabled={compDialog.submitting}
                    className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full"
                    data-testid="comp-submit-button"
                  >
                    {compDialog.submitting ? 'Issuing…' : 'Send Comp Tickets'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Guest List Dialog */}
        <Dialog
          open={guestListDialog.open}
          onOpenChange={(open) => !open && setGuestListDialog((s) => ({ ...s, open: false, editing: null }))}
        >
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="guest-list-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#1F3D2B] flex items-center gap-2">
                <Users className="w-5 h-5" /> Guest List
                {guestListDialog.event && (
                  <span className="text-sm font-sans font-normal text-[#4A5D50]">
                    · {guestListDialog.event.title}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Searchable list of every ticket for this event. Edit guest details or export to CSV.
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center gap-3 py-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5D50]" />
                <Input
                  placeholder="Search by name, email, or ticket code…"
                  value={guestListDialog.search}
                  onChange={(e) => setGuestListDialog((s) => ({ ...s, search: e.target.value }))}
                  className="pl-9"
                  data-testid="guest-list-search"
                />
              </div>
              <span className="text-xs text-[#4A5D50] whitespace-nowrap" data-testid="guest-list-counts">
                {guestListDialog.total_checked_in} / {guestListDialog.rows.length} checked in
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={exportGuestListCSV}
                disabled={!guestListDialog.rows.length}
                className="rounded-full"
                data-testid="guest-list-export-csv"
              >
                Export Guests CSV
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (!guestListDialog.event?.event_id) return;
                  window.location.href = `${BACKEND_URL}/api/organiser/events/${guestListDialog.event.event_id}/orders.csv`;
                }}
                disabled={!guestListDialog.event}
                className="rounded-full"
                data-testid="guest-list-export-orders-csv"
              >
                Export Orders CSV
              </Button>
            </div>

            <div className="overflow-y-auto border border-[#E5E9E6] rounded-lg" data-testid="guest-list-body">
              {guestListDialog.loading ? (
                <p className="p-8 text-center text-sm text-[#4A5D50]">Loading guests…</p>
              ) : guestListDialog.rows.length === 0 ? (
                <p className="p-8 text-center text-sm text-[#4A5D50]">No tickets sold yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F6F3] text-[10px] uppercase tracking-widest text-[#4A5D50] sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Guest</th>
                      <th className="text-left px-3 py-2 hidden md:table-cell">Email</th>
                      <th className="text-left px-3 py-2 hidden sm:table-cell">Ticket Type</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-right px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {guestListDialog.rows
                      .filter((r) => {
                        const q = guestListDialog.search.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          (r.guest_name || '').toLowerCase().includes(q) ||
                          (r.guest_email || '').toLowerCase().includes(q) ||
                          (r.ticket_code || '').toLowerCase().includes(q)
                        );
                      })
                      .map((row) => {
                        const isEditing = guestListDialog.editing?.ticket_code === row.ticket_code;
                        return (
                          <tr key={row.ticket_code} className="border-t border-[#E5E9E6]" data-testid={`guest-row-${row.ticket_code}`}>
                            <td className="px-3 py-2">
                              {isEditing ? (
                                <Input
                                  value={guestListDialog.editing.name}
                                  onChange={(e) => setGuestListDialog((s) => ({ ...s, editing: { ...s.editing, name: e.target.value } }))}
                                  className="h-8 text-sm"
                                  data-testid={`guest-edit-name-${row.ticket_code}`}
                                />
                              ) : (
                                <div className="flex flex-col">
                                  <span className="text-[#112318] font-medium">{row.guest_name}</span>
                                  <span className="text-[10px] font-mono text-[#9AA89F]">{row.ticket_code}</span>
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 hidden md:table-cell">
                              {isEditing ? (
                                <Input
                                  type="email"
                                  value={guestListDialog.editing.email}
                                  onChange={(e) => setGuestListDialog((s) => ({ ...s, editing: { ...s.editing, email: e.target.value } }))}
                                  className="h-8 text-sm"
                                  data-testid={`guest-edit-email-${row.ticket_code}`}
                                  placeholder="optional"
                                />
                              ) : (
                                <span className="text-[#4A5D50] text-xs">{row.guest_email || '—'}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 hidden sm:table-cell text-xs text-[#4A5D50]">
                              <div className="flex items-center gap-1.5">
                                <span>{row.ticket_type}</span>
                                {row.is_comp && (
                                  <span className="text-[9px] uppercase tracking-widest bg-[#C9A27A]/20 text-[#9C7434] px-1.5 py-0.5 rounded">
                                    Comp
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              {row.checked_in ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                                  <CheckCircle2 className="w-3 h-3" /> Checked in
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-stone-50 text-stone-600 border border-stone-200">
                                  Awaiting
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {isEditing ? (
                                <div className="flex gap-1 justify-end">
                                  <Button size="sm" variant="ghost" onClick={cancelEditGuest} disabled={guestListDialog.saving}>
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={saveGuestEdit}
                                    disabled={guestListDialog.saving}
                                    className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full"
                                    data-testid={`guest-edit-save-${row.ticket_code}`}
                                  >
                                    {guestListDialog.saving ? '…' : 'Save'}
                                  </Button>
                                </div>
                              ) : row.cancelled ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200">
                                  Cancelled{row.refund_status === 'approved' || row.refund_status === 'paid' ? ' · Refunded' : ''}
                                </span>
                              ) : (
                                <div className="flex gap-1 justify-end">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => startEditGuest(row)}
                                    className="text-[#1F3D2B] hover:bg-[#F4F6F3]"
                                    aria-label="Edit guest"
                                    title="Edit guest"
                                    data-testid={`guest-edit-button-${row.ticket_code}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openCancelTicket(row)}
                                    className="text-[#B04D4D] hover:bg-[#B04D4D]/10"
                                    aria-label="Cancel ticket"
                                    title="Cancel ticket"
                                    data-testid={`guest-cancel-button-${row.ticket_code}`}
                                  >
                                    <XIcon className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel Individual Ticket Dialog */}
        <Dialog
          open={cancelTicketDialog.open}
          onOpenChange={(open) => !open && setCancelTicketDialog({ open: false, row: null, refund: true, reason: '', submitting: false })}
        >
          <DialogContent className="max-w-md" data-testid="cancel-ticket-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#1F3D2B] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" /> Cancel this ticket?
              </DialogTitle>
              <DialogDescription className="sr-only">
                Cancel a single ticket and optionally issue a refund.
              </DialogDescription>
            </DialogHeader>
            {cancelTicketDialog.row && (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                  <p className="font-medium">{cancelTicketDialog.row.guest_name}</p>
                  <p className="text-xs mt-0.5 font-mono text-amber-800">{cancelTicketDialog.row.ticket_code}</p>
                  <p className="text-xs mt-1">
                    {cancelTicketDialog.row.ticket_type}
                    {cancelTicketDialog.row.is_comp && <span className="ml-2 text-[10px] uppercase tracking-widest bg-[#C9A27A]/30 text-[#9C7434] px-1.5 py-0.5 rounded">Comp</span>}
                  </p>
                </div>

                {!cancelTicketDialog.row.is_comp && (
                  <label className="flex items-start gap-3 cursor-pointer select-none bg-[#F4F6F3] border border-[#E5E9E6] rounded-lg p-3">
                    <input
                      type="checkbox"
                      checked={cancelTicketDialog.refund}
                      onChange={(e) => setCancelTicketDialog((s) => ({ ...s, refund: e.target.checked }))}
                      className="mt-1 w-4 h-4 accent-[#1F3D2B]"
                      data-testid="cancel-ticket-refund-toggle"
                    />
                    <span className="text-sm">
                      <span className="block font-medium text-[#112318]">Issue a refund</span>
                      <span className="block text-xs text-[#4A5D50] mt-0.5">
                        The ticket price will be deducted from your balance. <strong>Curation/booking fees are non-refundable.</strong> Refunds processed within 7–10 business days.
                      </span>
                    </span>
                  </label>
                )}

                {cancelTicketDialog.row.is_comp && (
                  <div className="bg-[#C9A27A]/10 border border-[#C9A27A]/30 rounded-lg p-3 text-xs text-[#4A5D50]">
                    Comp tickets cannot be refunded. The seat will be freed and the guest will be notified.
                  </div>
                )}

                <div>
                  <Label htmlFor="cancel-ticket-reason" className="text-[#4A5D50] text-sm">Reason (optional, shared with the guest)</Label>
                  <Textarea
                    id="cancel-ticket-reason"
                    rows={3}
                    maxLength={500}
                    placeholder="e.g. Guest can no longer attend"
                    value={cancelTicketDialog.reason}
                    onChange={(e) => setCancelTicketDialog((s) => ({ ...s, reason: e.target.value }))}
                    className="mt-1"
                    data-testid="cancel-ticket-reason"
                  />
                </div>

                <div className="flex gap-2 justify-end pt-2">
                  <Button
                    variant="ghost"
                    onClick={() => setCancelTicketDialog({ open: false, row: null, refund: true, reason: '', submitting: false })}
                    disabled={cancelTicketDialog.submitting}
                  >
                    Keep ticket
                  </Button>
                  <Button
                    onClick={submitCancelTicket}
                    disabled={cancelTicketDialog.submitting}
                    className="bg-rose-600 text-white hover:bg-rose-700 rounded-full"
                    data-testid="cancel-ticket-confirm"
                  >
                    {cancelTicketDialog.submitting ? 'Cancelling…' : (cancelTicketDialog.refund && !cancelTicketDialog.row.is_comp ? 'Cancel & Refund' : 'Cancel ticket')}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Ticket Type Dialog */}
        <Dialog
          open={editTicketDialog.open}
          onOpenChange={(open) => !open && closeEditTicketDialog()}
        >
          <DialogContent className="max-w-md" data-testid="edit-ticket-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#1F3D2B]">Edit ticket type</DialogTitle>
              <DialogDescription className="text-sm text-[#4A5D50]">
                Update the name, price, capacity or description. Increasing capacity automatically
                notifies waitlisted guests for this ticket type.
              </DialogDescription>
            </DialogHeader>
            {editTicketDialog.ticket && (
              <form onSubmit={handleSaveEditTicket} className="space-y-4">
                <div>
                  <Label className="text-xs">Ticket name</Label>
                  <Input
                    value={editTicketDialog.form.type_name}
                    onChange={(e) => setEditTicketDialog((s) => ({ ...s, form: { ...s.form, type_name: e.target.value } }))}
                    required
                    data-testid="edit-ticket-name"
                  />
                </div>
                <label className="flex items-center gap-3 p-3 border border-[#E5E9E6] rounded-lg cursor-pointer hover:bg-[#FAFAF7]">
                  <input
                    type="checkbox"
                    checked={editTicketDialog.form.is_free}
                    onChange={(e) => setEditTicketDialog((s) => ({
                      ...s,
                      form: { ...s.form, is_free: e.target.checked, price: e.target.checked ? '0' : s.form.price },
                    }))}
                    className="w-4 h-4"
                    data-testid="edit-ticket-is-free"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#1F3D2B]">Free ticket (R0)</p>
                    <p className="text-xs text-[#4A5D50]">No booking fee charged for free tickets.</p>
                  </div>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {!editTicketDialog.form.is_free && (
                    <div>
                      <Label className="text-xs">Price (ZAR)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editTicketDialog.form.price}
                        onChange={(e) => setEditTicketDialog((s) => ({ ...s, form: { ...s.form, price: e.target.value } }))}
                        required
                        data-testid="edit-ticket-price"
                      />
                    </div>
                  )}
                  <div className={editTicketDialog.form.is_free ? 'col-span-2' : ''}>
                    <Label className="text-xs">Total quantity</Label>
                    <Input
                      type="number"
                      min={Number(editTicketDialog.ticket.sold || 0)}
                      value={editTicketDialog.form.quantity}
                      onChange={(e) => setEditTicketDialog((s) => ({ ...s, form: { ...s.form, quantity: e.target.value } }))}
                      required
                      data-testid="edit-ticket-quantity"
                    />
                    <p className="text-[11px] text-[#4A5D50] mt-1">
                      {Number(editTicketDialog.ticket.sold || 0)} already sold — minimum allowed.
                    </p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Description (optional)</Label>
                  <Input
                    value={editTicketDialog.form.description}
                    onChange={(e) => setEditTicketDialog((s) => ({ ...s, form: { ...s.form, description: e.target.value } }))}
                    data-testid="edit-ticket-description"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="ghost" onClick={closeEditTicketDialog} data-testid="edit-ticket-cancel">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={editTicketDialog.submitting}
                    className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full"
                    data-testid="edit-ticket-save"
                  >
                    {editTicketDialog.submitting ? 'Saving…' : 'Save changes'}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default OrganiserDashboard;
